import { FC, InputHTMLAttributes, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from 'src/utils/style'
import { Button } from 'src/components/button'

export const Input: FC<
  InputHTMLAttributes<HTMLInputElement> & {
    icon?: ReactNode
    iconPosition?: 'left' | 'right'
    onIconClick?: () => void
    label?: string
  }
> = ({ className, icon, iconPosition = 'right', onIconClick, label, ...props }) => {
  const { t } = useTranslation()
  const isIconLeft = iconPosition === 'left'
  const isIconRight = iconPosition === 'right'
  const isIconActionable = !!onIconClick

  return (
    <section className="flex w-full flex-col items-start">
      {label && <span className="mb-1 block text-sm font-medium text-primary">{label}</span>}
      <div className="relative w-full">
        <input
          className={cn([
            'w-full rounded-md border bg-surface p-2',
            icon && isIconLeft && 'pl-10',
            icon && isIconRight && 'pr-10',
            'text-text-primary placeholder:font-thin placeholder:text-text-placeholder',
            'invalid:outline-none invalid:ring-2 invalid:ring-error-400',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          ])}
          {...props}
        />
        {icon && (
          <Button
            variant="unstyled"
            iconOnly
            type="button"
            onClick={onIconClick}
            disabled={!isIconActionable}
            className={cn([
              'absolute inset-y-0 flex items-center',
              isIconLeft && 'left-0 pl-3',
              isIconRight && 'right-0 pr-3',
              isIconActionable && 'cursor-pointer transition-opacity hover:opacity-70',
              !isIconActionable && 'pointer-events-none',
            ])}
            aria-label={isIconActionable ? t('shared.iconAction') : undefined}
          >
            {icon}
          </Button>
        )}
      </div>
    </section>
  )
}
